import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { formatDateTime, formatDate } from 'utils/date';

import { queries } from 'RestAPIs/rapini';

import { Box, Chip } from '@mui/material';

import { DataGrid, DateNavigation } from 'components/shared';
import { faEye } from '@fortawesome/pro-light-svg-icons';
import { AskedDutyResponse } from 'generated/index';
import DutyRoundsDialog from './DutyRoundsDialog';
import { GridColumns, GridValidRowModel } from '@mui/x-data-grid-pro';
import {
  BLUE,
  BLUE_DARK,
  BLUE_SECONDARY,
  BLUE_TERTIARY,
  GRAY_LIGHT_SECONDARY,
  GRAY_SECONDARY,
  PRIMARY,
  PRIMARY_LIGHT,
  RED,
  RED_LIGHT
} from 'constants/colors';
import { useNavParams } from 'hooks';
import { getAskedDutiesByRequestId } from './utils';

const AutoAskDutyRoundsOverview = () => {
  const { t } = useTranslation('common');
  const { params } = useNavParams();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { data: askedDuties, isLoading } = queries.useGetAskedDuties(
    dayjs(selectedDate).format('YYYY-MM-DD'),
    dayjs(selectedDate).format('YYYY-MM-DD'),
    undefined
  );

  const [selectedAskedDuty, setSelectedAskedDuty] =
    useState<AskedDutyResponse | null>(null);

  const onViewClick = (askedDutyId: number) => {
    const askedDuty = askedDuties?.find(
      (d) => d.duty.requestId === askedDutyId
    );
    if (askedDuty) {
      setSelectedAskedDuty(askedDuty);
    }
  };

  const handleClose = () => {
    setSelectedAskedDuty(null);
  };

  const askedDutyStatusColor = {
    ASKED: {
      label: t('askedDutyStatus.asked'),
      style: { backgroundColor: BLUE_TERTIARY, color: BLUE }
    },
    PLANNED: {
      label: t('askedDutyStatus.resolved'),
      style: { backgroundColor: BLUE_SECONDARY, color: BLUE_DARK }
    },
    ONE_REPLIED_AVAILABLE: {
      label: t('askedDutyStatus.available'),
      style: { backgroundColor: PRIMARY_LIGHT, color: PRIMARY }
    },
    ALL_REPLIED_NOT_AVAILABLE: {
      label: t('askedDutyStatus.notAvailable'),
      style: { backgroundColor: RED_LIGHT, color: RED }
    },
    FAILED: {
      label: t('askedDutyStatus.notResolved'),
      style: { backgroundColor: RED_LIGHT, color: RED }
    },
    OPEN: {
      label: t('askedDutyStatus.open'),
      style: { backgroundColor: GRAY_LIGHT_SECONDARY, color: GRAY_SECONDARY }
    }
  };

  const renderAskedDutyStatusColumn = (params) => {
    const value = params.value;
    const statusConfig =
      askedDutyStatusColor[value] || askedDutyStatusColor.OPEN;
    return (
      <Chip
        label={statusConfig.label}
        size="small"
        style={statusConfig.style}
      />
    );
  };

  const columns: GridColumns<GridValidRowModel> = [
    { field: 'id', headerName: t('number'), minWidth: 100 },
    {
      field: 'date',
      headerName: t('date'),
      minWidth: 80,
      renderCell: ({ value }) => formatDate(value)
    },
    { field: 'start', headerName: t('startTime'), minWidth: 70 },
    { field: 'end', headerName: t('endTime'), minWidth: 70 },
    { field: 'dutyCode', headerName: t('dutyCode'), minWidth: 80 },
    {
      field: 'organizationalUnit',
      headerName: t('organizationalUnit'),
      flex: 1
    },
    { field: 'function', headerName: t('function'), flex: 1 },
    { field: 'numberOfAskedEmployees', headerName: t('asked'), minWidth: 40 },
    {
      field: 'numberOfNotifiedEmployees',
      headerName: t('notified'),
      minWidth: 40
    },
    {
      field: 'numberOfRespondedEmployees',
      headerName: t('responded'),
      minWidth: 40
    },
    { field: 'employee', headerName: t('employee'), flex: 1 },
    {
      field: 'triggeredAt',
      headerName: t('triggeredAt'),
      minWidth: 120,
      renderCell: ({ value }) => formatDateTime(value)
    },
    {
      field: 'status',
      headerName: t('status'),
      minWidth: 140,
      renderCell: (params) => renderAskedDutyStatusColumn(params)
    }
  ];

  const rows =
    askedDuties
      ?.sort(
        (a, b) =>
          new Date(a.duty.start).getTime() - new Date(b.duty.start).getTime()
      )
      .map((d) => ({
        id: d.duty.requestId,
        employee: d.duty.assignedEmployee?.nameInformal,
        date: d.duty.start,
        start: dayjs(d.duty.start).format('HH:mm'),
        end: dayjs(d.duty.end).format('HH:mm'),
        dutyCode: d.duty.dutyCode?.dutyCode,
        organizationalUnit: d.duty.assignedOrganizationalUnit?.name,
        function: d.duty.function?.description,
        triggeredAt: d.triggeredAt,
        numberOfAskedEmployees: d.numberOfAskedEmployees,
        numberOfNotifiedEmployees: d.numberOfNotifiedEmployees,
        numberOfRespondedEmployees: d.numberOfRespondedEmployees,
        status: d.status
      })) ?? [];

  // Open duty modal based on query params passed to page
  useEffect(() => {
    const requestId = params?.request;

    if (requestId && (askedDuties || []).length > 0) {
      setSelectedAskedDuty(getAskedDutiesByRequestId(askedDuties, requestId));
    }
  }, [params?.request]);

  // Set data navigation based on query params passed to page
  useEffect(() => {
    const date =
      params?.date && dayjs(params.date, 'DD-MM-YYYY').isValid()
        ? dayjs(params.date, 'DD-MM-YYYY').toDate()
        : null;

    if (date) setSelectedDate(date);
  }, [params?.date]);

  return (
    <Box p={2}>
      <Box py={2}>
        <DateNavigation
          defaultPeriod="day"
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
      </Box>
      <DataGrid
        columns={columns}
        rows={rows}
        loading={isLoading}
        customActions={(id) => [
          {
            tooltipTitle: t('view'),
            icon: faEye,
            onClick: () => onViewClick(id as number)
          }
        ]}
      />
      {selectedAskedDuty && (
        <DutyRoundsDialog
          open={!!selectedAskedDuty}
          askedDuty={selectedAskedDuty}
          handleClose={handleClose}
        />
      )}
    </Box>
  );
};

export default AutoAskDutyRoundsOverview;
